/**
 * The idle world's client state. The sim runs here, optimistically: an action changes the
 * world on screen at once, and the same intent goes to `idleSync` (the seam, `sync.ts`),
 * whose answer replaces it when it comes.
 *
 * Presence (D242): while the app is open and visible the player is here. Opening it, coming
 * back to it, every action and a heartbeat check the player in, so the barn's cap is for
 * time away only. The welcome back comes from the server's open, computed before its
 * check-in.
 */
import { create } from 'zustand';
import { getIdleContent, type IdleContent, type IdleDecorationKind } from '@hedgerow/data';
import { setThemeContent } from './terms.ts';
import {
  advanceIdle,
  applyIdleIntent,
  checkInIdle,
  idleArrangeOpen,
  idleGoalsNewlyMet,
  idleLayout,
  idleMoveCheck,
  type IdleLayout,
  type IdleMoveCheck,
  type IdleMoveTarget,
  type IdleWorldState,
  type IdleFestivalRecord,
  type IdleIntent,
  type IdleResult,
  type IdleWelcomeBack,
  type IdleRegionPublic,
  type IdleRegionView,
} from '@hedgerow/sim';
import { now } from '../clock.ts';
import { clearNotices } from '../juice/index.ts';
import {
  idleSync,
  type IdleCreateResult,
  type IdleWorldEntry,
  type IdleNoWorld,
  type IdleOpened,
} from './sync.ts';
import { showWelcome } from './daily-view.ts';
import { newSeenNow, type NewSeen } from './badges.ts';
import { defaultWorld, setDefaultWorld } from './world-pref.ts';
import { knownGoalKeys, shouldWelcome, welcomeWaste, type Banked, type TipId } from './view.ts';

export let idleContent: IdleContent = getIdleContent();
setThemeContent(idleContent);

/** D348: load the shown world's own theme content; called whenever a world's data arrives. */
function loadTheme(theme?: string): void {
  idleContent = getIdleContent(theme);
  setThemeContent(idleContent);
}

export type IdleTab = 'world' | 'almanac' | 'festival' | 'region';

/** While the app is open and visible, the player is checked in this often (D242). */
export const HEARTBEAT_MS = 45_000;

/** How long the barn chip says what a return banked. */
export const BANKED_SHOW_MS = 8_000;

/** The chip's own share of a welcome back, shown until `t + BANKED_SHOW_MS` (D324). */
const bankedWaste = (w: IdleWelcomeBack, t: number) => {
  const { lostMs, lostShare } = welcomeWaste(w);
  return { until: t + BANKED_SHOW_MS, lostMs, lostShare };
};

/** How long the new festival mark is drawn going down after the festival (D259). */
export const MARK_PULSE_MS = 4_000;

/** A tick further apart than this means the page was asleep: treat it as a return. */
export const SLEEP_GAP_MS = 60_000;

const TIPS_KEY = 'hedgerow-idle-tips';
/**
 * D460: the Heritage earned when the player last opened Perks, per world, on this device.
 * A world with no record (a new phone, or the first open after this shipped) takes what it has
 * now, so nothing shows as unseen from nowhere; a lower figure than the record starts it again.
 */
const HERITAGE_SEEN_PREFIX = 'hedgerow-idle-heritage-seen-';

/**
 * D461: what was last seen of goals, rosettes and the week's prize, per world, on this device.
 * Like Heritage, a world with no record starts from what it has now, so nothing is new from
 * nowhere on a new phone.
 */
const NEW_SEEN_PREFIX = 'hedgerow-idle-new-seen-';

function newSeenOf(worldId: string, world: IdleWorldState, t: number): NewSeen {
  try {
    const raw = readStore(NEW_SEEN_PREFIX + worldId);
    const r = raw ? (JSON.parse(raw) as Partial<NewSeen>) : null;
    if (r && Array.isArray(r.goals) && typeof r.rosettes === 'number' && typeof r.week === 'number')
      return { goals: r.goals, rosettes: r.rosettes, week: r.week };
  } catch {
    // A broken record is no record.
  }
  const now = newSeenNow(world, idleContent, t);
  writeStore(NEW_SEEN_PREFIX + worldId, JSON.stringify(now));
  return now;
}

function heritageSeenOf(worldId: string, world: IdleWorldState): number {
  const earned = world.permanent.heritageEarned;
  const raw = readStore(HERITAGE_SEEN_PREFIX + worldId);
  const seen = raw === null ? NaN : Number(raw);
  if (Number.isFinite(seen) && seen <= earned) return seen;
  writeStore(HERITAGE_SEEN_PREFIX + worldId, String(earned));
  return earned;
}
/**
 * D336: the "Heritage to spend" bar's dismissal, per world id: the perk ids affordable at the
 * time it was dismissed. The bar returns once one not in that list becomes affordable.
 */
const HERITAGE_DISMISS_PREFIX = 'hedgerow-idle-heritage-dismiss-';

function readHeritageDismiss(worldId: string): string[] | null {
  try {
    const raw = readStore(HERITAGE_DISMISS_PREFIX + worldId);
    return raw ? (JSON.parse(raw) as string[]) : null;
  } catch {
    return null;
  }
}

function writeHeritageDismiss(worldId: string, affordable: string[]): void {
  writeStore(HERITAGE_DISMISS_PREFIX + worldId, JSON.stringify(affordable));
}

function readStore(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function writeStore(key: string, value: string): void {
  try {
    globalThis.localStorage?.setItem(key, value);
  } catch {
    // Per device and optional: losing it only shows a tip again.
  }
}

function readTips(): Set<TipId> {
  try {
    const raw = readStore(TIPS_KEY);
    return new Set(raw ? (JSON.parse(raw) as TipId[]) : []);
  } catch {
    return new Set();
  }
}

/** D325: what passing the world on paid, for the screen after it. */
export interface PassedOn {
  lineage: number;
  fromHeritage: number;
  fromPlay: number;
  generation: number;
}

/** D454 step 3: a move made while arranging; sent as an intent on Done. */
export type ArrangeMove =
  { producer: string; with: string } | { producer: string; x: number; y: number };

/**
 * D454 step 3: arrange mode. The draft layout is what the map draws; nothing is stored or sent
 * until Done. `held` is the producer whose plot is lifted, `target` the spot under the finger
 * and `check` the sim's `idleMoveCheck` for it.
 */
export interface ArrangeState {
  layout: IdleLayout;
  moves: ArrangeMove[];
  held: string | null;
  target: IdleMoveTarget | null;
  check: IdleMoveCheck | null;
  /** The one-time key shows: this is the first time arrange has opened on this device. */
  key: boolean;
}

/** The world as arrange mode draws it: the live world with the draft layout in place. */
export function arrangedWorld(world: IdleWorldState, arrange: ArrangeState): IdleWorldState {
  return { ...world, layout: arrange.layout };
}

export interface IdleStore {
  world: IdleWorldState | null;
  /** D312: the world's own name, shown in the header and settings. */
  worldName: string;
  /** D336: the account's current world id, to key screen state kept per world. Null until the
   * first open answers (the local stub world is `'local'`). */
  worldId: string | null;
  /** D425: the open world's theme id, which picks its accent and coin colours. */
  theme: string;
  /**
   * D336: perk ids affordable when the "Heritage to spend" bar was last dismissed on this
   * world. Null: never dismissed, so the bar shows whenever Heritage could buy a perk level.
   * Dismissed, it returns only once a perk not in this list is affordable.
   */
  heritageDismissed: string[] | null;
  /** Dismiss the "Heritage to spend" bar until a perk not in `affordable` is affordable. */
  dismissHeritage(affordable: string[]): void;
  /** The app clock at the last tick. */
  now: number;
  tab: IdleTab;
  settingsOpen: boolean;
  barnOpen: boolean;
  /** UI step 3: the Offers and bonuses sheet is open. */
  offersOpen: boolean;
  welcome: IdleWelcomeBack | null;
  /** D416: the landing is still over the app (booting or choosing a world); the welcome back waits. */
  landingOpen: boolean;
  /** What the last return banked, for the barn chip. */
  banked: Banked | null;
  /** Goals met while away, named on the welcome back. */
  welcomeGoals: string[];
  /** D459: the last welcome back that found the barn full, kept so the barn sheet shows what it cost. */
  barnWelcome: IdleWelcomeBack | null;
  /** Goals met while playing, waiting to go into the World tab's notice queue (D260). */
  goalsMet: string[];
  /** The World tab has queued these goals: clear them. */
  takeGoals(): void;
  /** The festival just held, for its fanfare. */
  festival: IdleFestivalRecord | null;
  /** D257: the world just passed on, for its fanfare. */
  /** D325: the world just passed on: the Lineage it paid, and from where. */
  passedOn: PassedOn | null;
  showPassedOn(p: PassedOn): void;
  closePassedOn(): void;
  /** D259: the newest festival mark, drawn going down on the map until `until`. */
  markPulse: { index: number; until: number } | null;
  showFestival(r: IdleFestivalRecord): void;
  closeFestival(): void;
  tipsSeen: Set<TipId>;
  /** D460: the Heritage earned when Perks was last opened, on this device; null before a world. */
  heritageSeen: number | null;
  /** D461: what was last seen of goals, rosettes and the week's prize; null before a world. */
  newSeen: NewSeen | null;
  /**
   * D365: producer and building row ids that have shown on the World tab this year, so a row
   * stays once it has appeared even if the balance then falls under a quarter of its price.
   * Reset whenever the year it was kept for is no longer the world's current one.
   */
  seenRows: Set<string>;
  seenRowsYear: number | null;
  /** A row has shown for `year`: keep it shown even if it stops being affordable this year. */
  seeRow(id: string, year: number): void;
  worlds: IdleWorldEntry[];
  /** The world's region as the last response gave it, and its table once fetched. */
  region: IdleRegionView | null;
  regionTable: IdleRegionPublic | null;
  /** The server has not answered the first open. */
  offline: boolean;
  /**
   * D292: photo mode. The screens hide their own chrome; the 3D lane frames the world, saves
   * or shares the picture, and reads this flag the same way, through the store, not a prop.
   */
  photoMode: boolean;
  enterPhotoMode(): void;
  exitPhotoMode(): void;
  /**
   * D331: the Decorations sheet is open under the map, and what is being tried on. The map
   * draws the world with `decorPreview` shown (`idlePreviewDecorations`); nothing is stored.
   */
  decorOpen: boolean;
  decorPreview: Partial<Record<IdleDecorationKind, string>>;
  setDecorOpen(open: boolean): void;
  /** D454 step 3: the map's ••• sheet ("The farm": Arrange, Decorate, Photo) is open. */
  farmOpen: boolean;
  setFarmOpen(open: boolean): void;
  /** Try a decoration on, or take the try off its kind with `null`. */
  previewDecoration(kind: IdleDecorationKind, id: string | null): void;
  /**
   * D454 step 3: arrange mode, or null. The map reads `useShownWorld()` (the draft layout in
   * place) and calls `pickUp`, `hover`, `drop` and `putBack`; the mode bar and dock call the rest.
   */
  arrange: ArrangeState | null;
  /** Open arrange mode. False when plots cannot be moved yet (before generation 2). */
  enterArrange(): boolean;
  /** Lift a producer's plot. False when it has no plot to move. Replaces any plot held. */
  pickUp(producer: string): boolean;
  /** The spot under the finger (another producer's plot, or free ground), or null for none. */
  hover(target: IdleMoveTarget | null): IdleMoveCheck | null;
  /** Put the held plot down on the hovered spot. Refused with the sim's reason when blocked. */
  drop(): { ok: true } | { ok: false; reason: string };
  /** Let go of the held plot where it was. Nothing changes. */
  putBack(): void;
  /** Leave arrange mode and throw every move of this session away. */
  cancelArrange(): void;
  /** Leave arrange mode and send the moves, in order. */
  doneArrange(): void;
  /**
   * D266: there is no world to open, so the first run is shown. `sign_in`: this device has
   * signed in before and its session has ended (D190).
   */
  firstRun: IdleNoWorld['needs'] | null;
  /** The first run's Continue: make the world under `name`, in `theme` (D359), and open it. */
  createWorld(name: string, mapSeed?: string, theme?: string): Promise<IdleCreateResult>;
  /**
   * D337: the first run's naming and seed screens are open over a world the player already
   * has, from settings' "Start a new world".
   */
  newWorldOpen: boolean;
  setNewWorldOpen(open: boolean): void;
  /** D337: open another of the player's idle worlds. Null when it worked; otherwise the reason. */
  switchWorld(worldId: string): Promise<string | null>;
  /**
   * D337: delete one of the player's worlds. Deleting the open one moves to another, or to the
   * first run when none is left. Null when it went; otherwise the reason to show.
   */
  deleteWorld(worldId: string): Promise<string | null>;
  init(): Promise<void>;
  tick(): void;
  /** The page is visible again. */
  resume(): void;
  /** Run an intent: at once here, then through the seam. The local result is returned. */
  act(intent: IdleIntent): IdleResult;
  setTab(tab: IdleTab): void;
  setSettingsOpen(open: boolean): void;
  setBarnOpen(open: boolean): void;
  setOffersOpen(open: boolean): void;
  closeWelcome(): void;
  setLandingOpen(open: boolean): void;
  /** A tip has been shown: it is counted seen at once and never shows again (D261). */
  seeTip(id: TipId): void;
  /** D460: Perks is open, so the Heritage earned so far counts as seen. */
  seeHeritage(): void;
  /** D461: Goals was left, a rosettes sheet or the week's sheet opened: that part is seen now. */
  seeNew(part: 'goals' | 'rosettes' | 'week'): void;
  loadWorlds(): Promise<void>;
  loadRegion(): Promise<void>;
  /** D312: rename the current world, with the same checks as naming a new one (D267). Null
   * when it worked; otherwise the reason to show. */
  renameWorld(name: string, worldId?: string): Promise<string | null>;
}

export const useIdle = create<IdleStore>((set, get) => {
  /** Intents sent so far, so a late answer never overwrites a newer action. */
  let sent = 0;
  /** Intents not yet answered. An open that lands while any is out is not taken. */
  let pending = 0;
  let opening = false;
  /** D337: bumped when the open world changes, so a late answer for the old world is dropped. */
  let gen = 0;

  /** Queue for celebration every goal `next` has met that the world on screen had not. */
  const goalsFrom = (next: IdleWorldState): string[] => {
    const prev = get().world;
    return prev ? idleGoalsNewlyMet(prev, next) : [];
  };
  const withGoals = (next: IdleWorldState) => {
    const fresh = goalsFrom(next);
    return fresh.length
      ? { world: next, goalsMet: [...get().goalsMet, ...fresh] }
      : { world: next };
  };

  /** Take the server's open: its world, and the welcome back when the player was away. */
  const take = (o: IdleOpened, sentAtStart: number, returning: boolean) => {
    const t = now();
    if (pending > 0 || sent !== sentAtStart) return;
    // D348: the world's own theme picks its content; a switch to a world of another theme
    // reloads it before anything here reads `idleContent`.
    loadTheme(o.theme);
    const w = o.welcome;
    // The heartbeat is a player who never left: no fanfare, whatever the server counted.
    // D304: the day's first open shows its gift on the same card, even after a short time away.
    const back = returning && !!w && showWelcome(w);
    const next = advanceIdle(o.world, idleContent, t);
    const fresh = goalsFrom(next);
    // D336: a world switch (or the first open) loads that world's own Heritage dismissal.
    const newWorld = !!o.worldId && o.worldId !== get().worldId;
    set({
      world: next,
      worldName: o.name,
      theme: o.theme ?? 'farm',
      ...(newWorld
        ? {
            worldId: o.worldId!,
            heritageDismissed: readHeritageDismiss(o.worldId!),
            heritageSeen: heritageSeenOf(o.worldId!, next),
            newSeen: newSeenOf(o.worldId!, next, t),
          }
        : {}),
      // Goals met while away are named on the welcome back; otherwise each is celebrated.
      // D335: `fresh` is empty when the gap settled a backlog under older goal rules, so the
      // welcome back is never filled with goals met quietly; `knownGoalKeys` also drops any key
      // the content no longer has a goal for, so a raw id is never stored to show.
      ...(fresh.length
        ? back
          ? { welcomeGoals: knownGoalKeys(idleContent, w?.goals?.length ? w.goals : fresh) }
          : { goalsMet: [...get().goalsMet, ...fresh] }
        : back
          ? { welcomeGoals: knownGoalKeys(idleContent, w?.goals ?? []) }
          : {}),
      now: t,
      offline: false,
      firstRun: null,
      ...(o.region ? { region: o.region } : {}),
      ...(back && w
        ? {
            welcome: w,
            ...(shouldWelcome(w)
              ? {
                  // D324: how much of the night was lost, worked out once here.
                  banked: {
                    away: Math.min(w.away, w.capacity),
                    capped: w.capped,
                    ...bankedWaste(w, t),
                  },
                }
              : {}),
          }
        : {}),
    });
  };

  /** Open on the server: boot and a return (`returning`), and the heartbeat. */
  const open = async (returning: boolean) => {
    if (opening) return;
    opening = true;
    const at = sent;
    const g = gen;
    try {
      const o = await idleSync.open();
      if (g !== gen) return;
      if (o && 'needs' in o) {
        if (!get().world) set({ firstRun: o.needs, offline: false });
      } else if (o) take(o, at, returning);
      else if (!get().world) set({ offline: true });
    } finally {
      if (g === gen) opening = false;
    }
  };

  /**
   * D337: put the open world down before another is opened. Everything on screen that belongs
   * to one world goes, so nothing of it is drawn, celebrated or compared against the next one;
   * an answer still out for it is dropped.
   */
  const leaveWorld = () => {
    clearNotices();
    gen++;
    opening = false;
    set({
      world: null,
      worldName: '',
      region: null,
      regionTable: null,
      welcome: null,
      banked: null,
      welcomeGoals: [],
      barnWelcome: null,
      goalsMet: [],
      festival: null,
      passedOn: null,
      markPulse: null,
      decorOpen: false,
      decorPreview: {},
      arrange: null,
      farmOpen: false,
      barnOpen: false,
      offersOpen: false,
      settingsOpen: false,
      tab: 'world',
      seenRows: new Set(),
      seenRowsYear: null,
    });
  };

  return {
    world: null,
    worldName: '',
    worldId: null,
    theme: 'farm',
    heritageDismissed: null,
    dismissHeritage: (affordable) => {
      const id = get().worldId;
      if (id) writeHeritageDismiss(id, affordable);
      set({ heritageDismissed: affordable });
    },
    now: now(),
    tab: 'world',
    settingsOpen: false,
    barnOpen: false,
    offersOpen: false,
    welcome: null,
    landingOpen: true,
    banked: null,
    welcomeGoals: [],
    barnWelcome: null,
    goalsMet: [],
    festival: null,
    markPulse: null,
    passedOn: null,
    tipsSeen: readTips(),
    heritageSeen: null,
    newSeen: null,
    seenRows: new Set(),
    seenRowsYear: null,
    seeRow(id, year) {
      const { seenRows, seenRowsYear } = get();
      if (seenRowsYear !== year) set({ seenRows: new Set([id]), seenRowsYear: year });
      else if (!seenRows.has(id)) set({ seenRows: new Set([...seenRows, id]) });
    },
    worlds: [],
    region: null,
    regionTable: null,
    offline: false,
    firstRun: null,
    newWorldOpen: false,
    photoMode: false,
    decorOpen: false,
    decorPreview: {},
    arrange: null,
    farmOpen: false,
    setFarmOpen: (farmOpen) => set({ farmOpen }),

    enterArrange() {
      const { world } = get();
      if (!world || !idleArrangeOpen(world, idleContent)) return false;
      const key = !get().tipsSeen.has('arrange');
      if (key) get().seeTip('arrange');
      set({
        decorOpen: false,
        decorPreview: {},
        farmOpen: false,
        arrange: {
          layout: idleLayout(world, idleContent),
          moves: [],
          held: null,
          target: null,
          check: null,
          key,
        },
      });
      return true;
    },
    pickUp(producer) {
      const { world, arrange } = get();
      if (!world || !arrange) return false;
      if (!arrange.layout.plots[producer] || !world.year.producers[producer]) return false;
      set({ arrange: { ...arrange, held: producer, target: null, check: null, key: false } });
      return true;
    },
    hover(target) {
      const { world, arrange } = get();
      if (!world || !arrange?.held) return null;
      if (!target) {
        if (arrange.target) set({ arrange: { ...arrange, target: null, check: null } });
        return null;
      }
      const was = arrange.target;
      if (was && JSON.stringify(was) === JSON.stringify(target)) return arrange.check;
      const check = idleMoveCheck(arrangedWorld(world, arrange), idleContent, arrange.held, target);
      set({ arrange: { ...arrange, target, check } });
      return check;
    },
    drop() {
      const { arrange } = get();
      const c = arrange?.check;
      if (!arrange?.held || !arrange.target || !c) return { ok: false, reason: 'no_plot' };
      if (!c.ok) return { ok: false, reason: c.reason };
      const move: ArrangeMove =
        'with' in arrange.target
          ? { producer: arrange.held, with: arrange.target.with }
          : { producer: arrange.held, x: arrange.target.x, y: arrange.target.y };
      set({
        arrange: {
          ...arrange,
          layout: c.layout,
          moves: [...arrange.moves, move],
          held: null,
          target: null,
          check: null,
        },
      });
      return { ok: true };
    },
    putBack() {
      const { arrange } = get();
      if (arrange) set({ arrange: { ...arrange, held: null, target: null, check: null } });
    },
    cancelArrange: () => set({ arrange: null }),
    doneArrange() {
      const { arrange } = get();
      set({ arrange: null });
      if (!arrange) return;
      // One intent each, in order: the seam sends one a time, so none comes near the envelope's
      // 20. Each is checked against the world as the one before it left it.
      for (const m of arrange.moves)
        get().act(
          'with' in m
            ? { type: 'swapPlots', producer: m.producer, with: m.with }
            : { type: 'movePlot', producer: m.producer, x: m.x, y: m.y },
        );
    },

    async init() {
      await open(true);
    },

    async createWorld(name, mapSeed, theme) {
      const r = await idleSync.create(name.trim(), mapSeed, theme);
      if (r === 'ok') {
        // A new world opens on the World tab at its first tip, even on a device that has
        // seen the tips on another world. D337: the world that was open is put down first.
        writeStore(TIPS_KEY, '[]');
        leaveWorld();
        set({ tipsSeen: new Set(), newWorldOpen: false });
        await open(false);
        if (!get().world) return 'failed';
      } else if (r === 'sign_in') set({ firstRun: 'sign_in' });
      return r;
    },

    setNewWorldOpen: (newWorldOpen) =>
      set({ newWorldOpen, ...(newWorldOpen ? { settingsOpen: false } : {}) }),

    async switchWorld(worldId) {
      if (worldId === get().worldId && get().world) return null;
      const reason = await idleSync.selectWorld(worldId);
      if (reason) return reason;
      // D337: the other world has earned on its own clock, as if the player had been away,
      // so it opens as a return, with its welcome back.
      leaveWorld();
      await open(true);
      return null;
    },

    async deleteWorld(worldId) {
      const reason = await idleSync.deleteWorld(worldId);
      if (reason) return reason;
      // D375: a deleted default world returns "Open on start" to "Ask each time".
      if (defaultWorld() === worldId) setDefaultWorld(null);
      if (worldId === get().worldId) {
        // D337: the open world went. The server has moved to another, or to none, and the
        // open goes there: a world with its welcome back, or the first run.
        leaveWorld();
        await open(true);
      } else await get().loadWorlds();
      return null;
    },

    tick() {
      const { world, now: last } = get();
      const t = now();
      if (!world) return set({ now: t });
      // A page that slept, or a clock moved on: a return, so the server says what was banked.
      if (t - last > SLEEP_GAP_MS) {
        set({ world: advanceIdle(world, idleContent, t), now: t });
        void open(true);
        return;
      }
      // The heartbeat: open and visible is present, so the barn's cap is for time away only.
      if (t - world.lastSeen >= HEARTBEAT_MS) {
        set({ ...withGoals(checkInIdle(world, idleContent, t)), now: t });
        void open(false);
        return;
      }
      set({ world: advanceIdle(world, idleContent, t), now: t });
    },

    resume() {
      void open(true);
    },

    act(intent) {
      const { world } = get();
      const t = now();
      if (!world) return { ok: false, reason: 'unknown_producer' };
      const r = applyIdleIntent(world, idleContent, intent, t);
      if (!r.ok) return r;
      set({ ...withGoals(r.state), now: t });
      const mine = ++sent;
      const g = gen;
      pending++;
      void idleSync
        .send(intent, t)
        .then((answer) => {
          // The server's world wins when it answers, unless the player has acted again
          // since: then the later answer carries this one too.
          const f = answer.ok === null ? undefined : answer.world;
          if (f && mine === sent && g === gen) set(withGoals(advanceIdle(f, idleContent, now())));
        })
        .finally(() => {
          pending--;
        });
      return r;
    },

    setTab: (tab) => {
      set({ tab });
      if (tab === 'region') void get().loadRegion();
    },
    setSettingsOpen: (settingsOpen) => {
      set({ settingsOpen });
      if (settingsOpen) void get().loadWorlds();
    },
    setBarnOpen: (barnOpen) => set(barnOpen ? { barnOpen } : { barnOpen, barnWelcome: null }),
    setOffersOpen: (offersOpen) => set({ offersOpen }),
    closeWelcome: () =>
      set((s) => ({
        welcome: null,
        welcomeGoals: [],
        barnWelcome: s.welcome?.capped ? s.welcome : null,
      })),
    setLandingOpen: (landingOpen) => set({ landingOpen }),
    takeGoals: () => set({ goalsMet: [] }),
    showFestival: (festival) => {
      // D374: a festival ends the year, so what was queued about it is stale.
      if (festival) clearNotices();
      set({ festival, ...(festival ? { goalsMet: [] } : {}) });
    },
    showPassedOn: (passedOn) => {
      if (passedOn) clearNotices();
      set({ passedOn, ...(passedOn ? { goalsMet: [] } : {}) });
    },
    // D388: the new generation starts on the world, once its traits are chosen.
    closePassedOn: () => set({ passedOn: null, tab: 'world' }),
    // The new year starts on the world, so the player sees it begin and the new mark go down.
    closeFestival: () => {
      const n = get().world?.permanent.festivals.length ?? 0;
      set({
        festival: null,
        tab: 'world',
        markPulse: n > 0 ? { index: n - 1, until: now() + MARK_PULSE_MS } : null,
      });
    },
    seeTip: (id) => {
      if (get().tipsSeen.has(id)) return;
      const tipsSeen = new Set(get().tipsSeen);
      tipsSeen.add(id);
      writeStore(TIPS_KEY, JSON.stringify([...tipsSeen]));
      set({ tipsSeen });
    },
    seeNew: (part) => {
      const { world, worldId, newSeen, now } = get();
      if (!world || !worldId || !newSeen) return;
      const fresh = newSeenNow(world, idleContent, now);
      const next: NewSeen = { ...newSeen, [part]: fresh[part] };
      if (JSON.stringify(next) === JSON.stringify(newSeen)) return;
      writeStore(NEW_SEEN_PREFIX + worldId, JSON.stringify(next));
      set({ newSeen: next });
    },
    seeHeritage: () => {
      const { world, worldId, heritageSeen } = get();
      if (!world || !worldId) return;
      const earned = world.permanent.heritageEarned;
      if (heritageSeen === earned) return;
      writeStore(HERITAGE_SEEN_PREFIX + worldId, String(earned));
      set({ heritageSeen: earned });
    },
    enterPhotoMode: () => set({ photoMode: true }),
    setDecorOpen: (open) => set({ decorOpen: open, decorPreview: {} }),
    previewDecoration: (kind, id) => {
      const next = { ...get().decorPreview };
      if (id === null) delete next[kind];
      else next[kind] = id;
      set({ decorPreview: next });
    },
    exitPhotoMode: () => set({ photoMode: false }),
    async loadWorlds() {
      // One more try when the server did not answer, and the last list is kept if neither does.
      const worlds = (await idleSync.listWorlds()) ?? (await idleSync.listWorlds());
      if (worlds) set({ worlds });
    },
    async renameWorld(name, worldId) {
      const reason = await idleSync.renameWorld(name, worldId);
      if (reason) return reason;
      // D459: a world named by id may be one that is not open; only the open one is on the HUD.
      const open = !worldId || get().worlds.find((f) => f.worldId === worldId)?.current !== false;
      if (open) set({ worldName: name });
      await get().loadWorlds();
      return null;
    },
    async loadRegion() {
      const id = get().region?.regionId;
      if (!id) return;
      const table = await idleSync.region(id);
      if (table) set({ regionTable: table, region: table });
    },
  };
});

// Test and dev hook, as the old store had: the e2e reads the world through it.
if (typeof window !== 'undefined') (window as unknown as { __idle?: unknown }).__idle = useIdle;
